import { FlexBox, Spacer } from '@airship-platform/jam-web/components';
import {
  PauseIcon,
  PlayIcon,
  SkipBackwardIcon,
  SkipForewardIcon,
} from '@airship-platform/jam-web/icons';
import Box, { BoxProps } from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { Appointment } from '../../../../data-access/appointments';
import ProposalAISummaryButton from '../buttons/ProposalAISummaryButton';

interface AudioPlayerProps extends BoxProps {
  url: string;
  summaries: Appointment['job']['callSummaries'];
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  url,
  summaries,
  ...rest
}) => {
  const SKIP_TIME = 15;
  const player = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [playError, setPlayError] = useState(false);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const handleSkipBackward = () => {
    if (player.current) {
      player.current.currentTime -= SKIP_TIME;
    }
  };

  const handleSkipForward = () => {
    if (player.current) {
      player.current.currentTime += SKIP_TIME;
    }
  };

  const handlePlayPause = () => {
    setPlayError(false);
    const playerElement = player.current;
    if (playerElement) {
      if (isPlaying) {
        playerElement.pause();
        setIsPlaying(false);
      } else {
        playerElement
          .play()
          .then(() => setIsPlaying(true))
          .catch(() => setPlayError(true));
      }
    }
  };

  useEffect(() => {
    const audioElement = player.current;

    if (audioElement) {
      const handleLoadedMetadata = () => {
        setDuration(audioElement.duration);
      };

      const handleTimeUpdate = () => {
        setCurrentTime(audioElement.currentTime);
        setProgress((audioElement.currentTime / audioElement.duration) * 100);

        if (audioElement.currentTime >= audioElement.duration) {
          setIsPlaying(false);
          setCurrentTime(0);
          setProgress(0);
        }
      };

      audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      audioElement.addEventListener('timeupdate', handleTimeUpdate);

      return () => {
        audioElement.removeEventListener(
          'loadedmetadata',
          handleLoadedMetadata
        );
        audioElement.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }
  }, []);

  return (
    <FlexBox
      border={(theme) => `1px solid ${theme.palette.gray?.[300]}`}
      borderRadius={3}
      overflow={'auto'}
      p={[1, 2]}
      flexDirection={'column'}
      gap={4}
    >
      <Box {...rest}>
        <Typography variant="body1">Customer call</Typography>
        <Box sx={{ mt: 2, minWidth: '100%' }}>
          <audio src={url} ref={player}></audio>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          color="softGold"
          sx={{
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              backgroundColor: 'softGold',
            },
          }}
        />
        {playError ? (
          <Typography
            component={'div'}
            variant="body4"
            color="error"
            sx={{ height: '2rem' }}
          >
            There was a problem playing this audio
          </Typography>
        ) : (
          <Spacer height={'2rem'} />
        )}
        <FlexBox justifyContent={'space-between'}>
          <Typography variant="body2">{formatTime(currentTime)}</Typography>
          <Typography variant="body2">{formatTime(duration)}</Typography>
        </FlexBox>
        <FlexBox alignItems={'center'} justifyContent={'center'}>
          <IconButton
            color="softGold"
            size="large"
            onClick={handleSkipBackward}
          >
            <SkipBackwardIcon />
          </IconButton>
          <IconButton size="large" onClick={handlePlayPause}>
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </IconButton>
          <IconButton color="softGold" size="large" onClick={handleSkipForward}>
            <SkipForewardIcon />
          </IconButton>
        </FlexBox>
        <ProposalAISummaryButton summaries={summaries} />
      </Box>
    </FlexBox>
  );
};

export default AudioPlayer;
